import { AudioEngine } from './audio';
import { InputController } from './input';
import { GhostPlayer, GhostRecorder } from './replay';
import { Fx } from './render/fx';
import { drawRider, drawRiderShadow, RIDER_STYLES } from './render/rider';
import { AmbientLayer, SkyLayer } from './render/sky';
import { WorldRenderer, type WorldFrame } from './render/road';
import { clamp, rgba, shade } from './render/palette';
import { roundRect } from './render/sprites';
import {
  FLOW_MAX,
  FLOW_SEND_COST,
  RULES_VERSION,
  SEGMENT_LENGTH,
  TICK_MS,
  UNITS_PER_METRE,
} from '../shared/constants';
import { getTheme, type Theme } from '../shared/themes';
import { Sim, TRICKS, livesForMode, type SimEvent } from '../shared/sim';
import { Track } from '../shared/track';
import type { GameMode, GhostFrame, RunSummary, ThemeId } from '../shared/types';

export interface StartOptions {
  mode: GameMode;
  theme: ThemeId;
  seed: string;
  length?: number;
  difficultyMul?: number;
  ghost?: GhostFrame[] | null;
  /** Race only: countdown target on the shared clock, and a rival feed. */
  startAt?: number;
  now?: () => number;
  rival?: () => { z: number; x: number; y: number; name: string } | null;
  onTick?: (sim: Sim) => void;
}

export type GameOverReason = 'finished' | 'dead' | 'quit';

export interface GameCallbacks {
  onEnd: (summary: RunSummary, ghost: GhostFrame[], sim: Sim, reason: GameOverReason) => void;
  onPause?: (paused: boolean) => void;
}

const MAX_FRAME_MS = 100;

export class Game {
  readonly canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  readonly input = new InputController();
  readonly audio = new AudioEngine();

  private world = new WorldRenderer();
  private sky = new SkyLayer();
  private ambient = new AmbientLayer();
  private fx = new Fx();
  private recorder = new GhostRecorder();
  private ghost: GhostPlayer | null = null;

  private sim: Sim | null = null;
  private theme: Theme = getTheme('redwood');
  private opts: StartOptions | null = null;
  private cb: GameCallbacks;

  private raf = 0;
  private lastFrame = 0;
  private accumulator = 0;
  private cameraX = 0;
  private pitch = 0;
  private grade = 0;
  private running = false;
  private paused = false;
  private countdownMs = 0;
  private endedAt = 0;

  private w = 0;
  private h = 0;
  private dpr = 1;
  private insetTop = 0;
  private insetBottom = 0;
  private lastFps = 60;
  private fpsAccum = 0;
  private fpsFrames = 0;

  constructor(canvas: HTMLCanvasElement, cb: GameCallbacks) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) throw new Error('2D canvas unavailable');
    this.ctx = ctx;
    this.cb = cb;
    this.input.attach(canvas);
  }

  setInsets(top: number, bottom: number): void {
    this.insetTop = top;
    this.insetBottom = bottom;
  }

  resize(): void {
    const rect = this.canvas.getBoundingClientRect();
    const cssW = Math.max(1, Math.round(rect.width));
    const cssH = Math.max(1, Math.round(rect.height));
    // Capping at 2 keeps a 3x iPhone from shading three times the pixels for no
    // visible gain on a canvas this stylised.
    this.dpr = Math.min(2, window.devicePixelRatio || 1);
    this.w = cssW;
    this.h = cssH;
    this.canvas.width = Math.round(cssW * this.dpr);
    this.canvas.height = Math.round(cssH * this.dpr);
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    this.sky.resize(cssW, cssH, this.theme);
    this.ambient.resize(cssW, cssH, this.theme);
  }

  get currentSim(): Sim | null {
    return this.sim;
  }

  get isRunning(): boolean {
    return this.running;
  }

  get fps(): number {
    return this.lastFps;
  }

  start(o: StartOptions): void {
    this.opts = o;
    this.theme = getTheme(o.theme);
    const track = new Track({
      seed: o.seed,
      theme: o.theme,
      mode: o.mode,
      length: o.length ?? (o.mode === 'endless' ? 0 : this.theme.length),
      difficultyMul: o.difficultyMul ?? 1,
    });
    this.sim = new Sim({ track, lives: livesForMode(o.mode) });
    this.recorder.reset();
    this.ghost = o.ghost && o.ghost.length > 1 ? new GhostPlayer(o.ghost) : null;
    this.fx.clear();
    this.cameraX = 0;
    this.accumulator = 0;
    this.lastFrame = 0;
    this.paused = false;
    this.endedAt = 0;
    this.countdownMs = o.startAt ? Math.max(0, o.startAt - (o.now?.() ?? Date.now())) : 2200;

    this.sky.resize(this.w, this.h, this.theme);
    this.ambient.resize(this.w, this.h, this.theme);
    this.input.reset();
    this.input.setEnabled(true);
    this.audio.startMusic(this.theme);
    this.audio.setMusicVolume(0.7);

    this.running = true;
    if (!this.raf) this.raf = requestAnimationFrame(this.frame);
  }

  stop(reason: GameOverReason = 'quit'): void {
    if (!this.running) return;
    this.running = false;
    this.input.setEnabled(false);
    this.audio.setSpeedRatio(0);
    if (this.raf) {
      cancelAnimationFrame(this.raf);
      this.raf = 0;
    }
    const sim = this.sim;
    if (sim && reason === 'quit') {
      this.cb.onEnd(sim.summary(this.opts?.mode ?? 'endless', RULES_VERSION), this.recorder.frames, sim, reason);
    }
  }

  setPaused(v: boolean): void {
    if (this.paused === v) return;
    this.paused = v;
    this.input.setEnabled(!v);
    this.audio.setMusicVolume(v ? 0.25 : 0.7);
    if (!v) this.lastFrame = 0;
    this.cb.onPause?.(v);
  }

  get isPaused(): boolean {
    return this.paused;
  }

  private frame = (now: number): void => {
    this.raf = requestAnimationFrame(this.frame);
    if (!this.sim || !this.opts) return;

    const dtMs = this.lastFrame ? Math.min(MAX_FRAME_MS, now - this.lastFrame) : TICK_MS;
    this.lastFrame = now;
    const dt = dtMs / 1000;

    this.fpsAccum += dtMs;
    this.fpsFrames++;
    if (this.fpsAccum >= 500) {
      this.lastFps = Math.round((this.fpsFrames * 1000) / this.fpsAccum);
      this.fpsAccum = 0;
      this.fpsFrames = 0;
    }

    if (!this.paused) {
      if (this.countdownMs > 0) {
        const before = Math.ceil(this.countdownMs / 1000);
        this.countdownMs -= dtMs;
        const after = Math.ceil(this.countdownMs / 1000);
        if (after !== before) this.audio.sfx(after <= 0 ? 'go' : 'countdown');
      } else if (this.running) {
        this.stepSim(dtMs);
      }
      this.fx.update(dt);
    }

    this.draw(dt, now / 1000);
  };

  private stepSim(dtMs: number): void {
    const sim = this.sim!;
    this.accumulator = Math.min(this.accumulator + dtMs, 250);
    const input = this.input.read();
    while (this.accumulator >= TICK_MS) {
      this.accumulator -= TICK_MS;
      const events = sim.step(input);
      if (events.length) this.handleEvents(events, sim);
      this.recorder.sample(sim);
      this.opts?.onTick?.(sim);
      if (sim.dead || sim.finished) break;
    }

    this.audio.setSpeedRatio(clamp(sim.speed / sim.maxSpeed, 0, 1) ** 1.6);
    this.audio.setIntensity(clamp(0.3 + sim.combo / 10 + (sim.sending ? 0.3 : 0), 0, 1));
    this.fx.setChroma(sim.sending ? 1 : 0);

    if ((sim.dead || sim.finished) && !this.endedAt) {
      this.endedAt = performance.now();
      this.running = false;
      this.input.setEnabled(false);
      window.setTimeout(() => {
        this.audio.setSpeedRatio(0);
        this.cb.onEnd(
          sim.summary(this.opts!.mode, RULES_VERSION),
          this.recorder.frames,
          sim,
          sim.finished ? 'finished' : 'dead',
        );
      }, sim.finished ? 900 : 1200);
    }
  }

  private handleEvents(events: SimEvent[], sim: Sim): void {
    const rx = this.w / 2;
    const ry = this.h * 0.66;
    for (const e of events) {
      switch (e.type) {
        case 'jump':
          this.audio.sfx('jump');
          this.fx.dust(rx, ry, 0.4, this.dustColor(), 0.1);
          break;
        case 'land':
          this.audio.sfx(e.hard ? 'landHard' : 'land');
          this.fx.burst(rx, ry, e.hard ? 14 : 7, this.dustColor(), { gravity: 400 });
          if (e.hard) this.fx.addShake(5);
          break;
        case 'ramp':
          this.audio.sfx('boost', { rate: 0.85 });
          this.fx.addShake(3);
          break;
        case 'orb':
          this.audio.sfx('orb', { rate: 1 + Math.min(1.4, e.combo * 0.09) });
          this.fx.burst(rx, ry - 40, 5, this.theme.accent, { gravity: -60, maxLife: 0.4 });
          break;
        case 'gear':
          this.audio.sfx('gear');
          this.fx.popup(rx, ry - 120, `GOLDEN GEAR +${e.points}`, '#ffd24d', 26);
          this.fx.addFlash('#ffd24d', 0.4);
          break;
        case 'boost':
          this.audio.sfx('boost');
          this.fx.addShake(4);
          break;
        case 'send':
          this.audio.sfx('send');
          this.fx.addShake(9);
          this.fx.popup(rx, ry - 150, 'SEND IT', this.theme.accent, 32);
          break;
        case 'sendEnd':
          this.audio.sfx('sendEnd');
          break;
        case 'nearMiss':
          this.audio.sfx('nearMiss', { pan: sim.x > 0 ? -0.6 : 0.6 });
          this.fx.popup(rx + (sim.x > 0 ? -70 : 70), ry - 60, 'CLOSE!', '#ffffff', 17);
          break;
        case 'trickStart':
          this.audio.sfx('trickStart');
          break;
        case 'trickLanded':
          this.audio.sfx('trickLand', { rate: e.clean ? 1 : 0.82 });
          this.fx.popup(rx, ry - 140, `${e.name.toUpperCase()} +${e.points}`, this.theme.accent2, 24);
          break;
        case 'trickSketchy':
          this.audio.sfx('sketchy');
          this.fx.popup(rx, ry - 100, 'SKETCHY', '#ff7a3c', 20);
          this.fx.addShake(5);
          break;
        case 'crash':
          this.audio.sfx('crash');
          this.audio.duck(600);
          this.fx.addShake(20, 3);
          this.fx.addFlash('#ff3b3b', 0.5);
          this.fx.burst(rx, ry, 34, this.dustColor(), { gravity: 700 });
          this.fx.popup(rx, ry - 90, 'CRASH', '#ff5a5a', 30);
          navigator.vibrate?.(90);
          break;
        case 'comboBreak':
          this.audio.sfx('comboBreak');
          break;
        case 'finish':
          this.audio.sfx('finish');
          this.fx.addFlash('#ffffff', 0.7);
          break;
        case 'dead':
          this.audio.stopMusic(0.8);
          break;
      }
    }
  }

  private dustColor(): string {
    switch (this.theme.id) {
      case 'antarctic':
        return '#e6f4ff';
      case 'volcano':
        return '#8a7060';
      case 'metro':
        return '#8f7fa8';
      case 'belt':
        return '#7cf7ff';
      case 'chroma':
        return '#ffd6ff';
      default:
        return '#8a6d4a';
    }
  }

  // --- drawing ---------------------------------------------------------

  private draw(dt: number, timeS: number): void {
    const sim = this.sim;
    if (!sim) return;
    const ctx = this.ctx;
    const { w, h } = this;

    // The trail holds still and the rider moves across it. Following his lane
    // position with the camera is what made steering read as scrolling the
    // background. The camera only catches up once he leaves the trail entirely.
    const strayed = clamp((Math.abs(sim.x) - 0.72) / 0.6, 0, 1);
    const follow = 0.14 + 0.72 * strayed;
    this.cameraX += (sim.x * follow - this.cameraX) * Math.min(1, 6 * dt);

    // Horizon rides the slope: it lifts as the trail drops away and drops back
    // over a roller, which is what actually sells "downhill".
    const gi = Math.floor(sim.z / SEGMENT_LENGTH);
    let gsum = 0;
    for (let i = 0; i < 26; i++) {
      const s = sim.track.segment(gi + i);
      gsum += -(s.yEnd - s.y) / SEGMENT_LENGTH;
    }
    this.grade += (gsum / 26 - this.grade) * Math.min(1, 3 * dt);
    const targetPitch = clamp(this.grade, -0.14, 0.28) * h * 0.7;
    this.pitch += (targetPitch - this.pitch) * Math.min(1, 2.5 * dt);
    const horizon = h / 2 - this.pitch;

    ctx.save();
    // The backing store can be a rounding-error wider than the CSS box, which
    // leaves an unpainted strip at the edge. Clear the whole thing, not `w`.
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = this.theme.sky.top;
    ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    this.fx.applyShake(ctx);

    const frame: WorldFrame = { horizon, sway: 0, riderX: w / 2, riderY: h * 0.8, riderRoadPx: w * 0.5, riderUnitPx: 0.1 };
    // Sky needs the horizon, the world needs to paint over it: draw sky first with
    // the previous frame's sway, which is imperceptible and avoids a second pass.
    this.sky.draw(ctx, this.theme, horizon, this.lastSway, timeS);

    const result = this.world.render(ctx, {
      track: sim.track,
      theme: this.theme,
      playerZ: sim.z,
      playerX: sim.x,
      cameraX: this.cameraX,
      playerY: sim.y,
      width: w,
      height: h,
      timeS,
      pitch: this.pitch,
      others: this.buildOthers(sim, timeS),
    });
    this.lastSway = result.sway;
    Object.assign(frame, result);

    const speedRatio = clamp(sim.speed / sim.maxSpeed, 0, 1);
    this.ambient.draw(ctx, this.theme, dt, speedRatio, result.sway);

    const groundY = frame.riderY + sim.y * frame.riderUnitPx;
    drawRiderShadow(ctx, frame.riderX, groundY, frame.riderUnitPx, sim.y);

    if (sim.grounded && !sim.dead) {
      this.fx.dust(frame.riderX, groundY, speedRatio * (sim.offroad ? 1.4 : 0.35), this.dustColor(), dt);
    }
    if (sim.onStairs) this.fx.addShake(1.6, 9);

    drawRider(ctx, {
      x: frame.riderX,
      y: frame.riderY,
      unitPx: frame.riderUnitPx,
      lean: sim.lean,
      trickPhase: sim.trickActive ? sim.trickPhase : 0,
      trickName: sim.trickActive ? TRICKS[sim.trickIndex]!.name : null,
      airborne: !sim.grounded,
      ghostly: sim.invulnMs > 0 ? 1 : 0,
      stunned: sim.stunMs > 0,
      sending: sim.sending,
      speedRatio,
      theme: this.theme,
      timeS,
      style: RIDER_STYLES.you!,
    });

    this.fx.drawParticles(ctx);
    ctx.restore();

    this.fx.drawOverlay(ctx, w, h, this.theme, speedRatio, timeS);
    this.fx.drawPopups(ctx);
    this.drawHud(ctx, sim, frame, timeS);
    if (this.countdownMs > 0) this.drawCountdown(ctx);
  }

  private lastSway = 0;

  private buildOthers(sim: Sim, timeS: number): NonNullable<Parameters<WorldRenderer['render']>[1]['others']> {
    const list: NonNullable<Parameters<WorldRenderer['render']>[1]['others']> = [];
    if (this.ghost) {
      const f = this.ghost.at(sim.timeMs);
      if (f) {
        list.push({
          z: f.z,
          x: f.x,
          y: f.y,
          draw: (ctx, sx, sy, unitPx) =>
            drawRider(ctx, {
              x: sx,
              y: sy,
              unitPx,
              lean: 0,
              trickPhase: 0,
              trickName: null,
              airborne: f.y > 4,
              ghostly: 0,
              stunned: false,
              sending: false,
              speedRatio: 0.5,
              theme: this.theme,
              timeS,
              style: RIDER_STYLES.ghost!,
              silhouette: true,
            }),
        });
      }
    }
    const rival = this.opts?.rival?.();
    if (rival) {
      list.push({
        z: rival.z,
        x: rival.x,
        y: rival.y,
        draw: (ctx, sx, sy, unitPx) =>
          drawRider(ctx, {
            x: sx,
            y: sy,
            unitPx,
            lean: 0,
            trickPhase: 0,
            trickName: null,
            airborne: rival.y > 4,
            ghostly: 0,
            stunned: false,
            sending: false,
            speedRatio: 0.7,
            theme: this.theme,
            timeS,
            style: RIDER_STYLES.rival!,
          }),
      });
    }
    return list;
  }

  private drawHud(ctx: CanvasRenderingContext2D, sim: Sim, frame: WorldFrame, timeS: number): void {
    const { w } = this;
    const top = this.insetTop + 10;
    ctx.save();
    ctx.textBaseline = 'top';

    // Score.
    ctx.textAlign = 'left';
    ctx.font = '900 30px "Arial Black", system-ui, sans-serif';
    outlined(ctx, String(Math.round(sim.score)).padStart(6, '0'), 16, top, '#ffffff', 30);

    ctx.font = '700 12px system-ui, sans-serif';
    const dist = sim.track.finite
      ? `${Math.round(sim.distanceMetres)} / ${Math.round(sim.track.worldLength / UNITS_PER_METRE)} m`
      : `${Math.round(sim.distanceMetres)} m`;
    outlined(ctx, dist, 17, top + 34, 'rgba(255,255,255,0.82)', 12);

    // Speed.
    ctx.textAlign = 'right';
    ctx.font = '900 26px "Arial Black", system-ui, sans-serif';
    const kmh = Math.round((sim.speed / UNITS_PER_METRE) * 3.6);
    outlined(ctx, String(kmh), w - 16, top, '#ffffff', 26);
    ctx.font = '700 11px system-ui, sans-serif';
    outlined(ctx, 'KM/H', w - 16, top + 30, 'rgba(255,255,255,0.7)', 11);

    // Gradient: the number that says out loud that this is a descent.
    const pct = Math.round(this.grade * 100);
    ctx.font = '800 14px system-ui, sans-serif';
    outlined(
      ctx,
      `${pct >= 0 ? '▼' : '▲'} ${Math.abs(pct)}%`,
      w - 16,
      top + 46,
      pct >= 8 ? this.theme.accent : 'rgba(255,255,255,0.75)',
      14,
    );

    // Flow meter.
    const fx0 = 16;
    const fy = top + 54;
    const fw = Math.min(190, w * 0.45);
    ctx.fillStyle = 'rgba(8,10,20,0.5)';
    roundRect(ctx, fx0, fy, fw, 9, 5);
    ctx.fill();
    const flowPct = sim.flow / FLOW_MAX;
    const ready = sim.flow >= FLOW_SEND_COST;
    const g = ctx.createLinearGradient(fx0, 0, fx0 + fw, 0);
    g.addColorStop(0, this.theme.accent2);
    g.addColorStop(1, this.theme.accent);
    ctx.fillStyle = ready ? g : 'rgba(255,255,255,0.42)';
    roundRect(ctx, fx0, fy, Math.max(2, fw * flowPct), 9, 5);
    ctx.fill();
    if (ready && !sim.sending) {
      ctx.strokeStyle = rgba('#ffffff', 0.4 + 0.4 * Math.sin(timeS * 6));
      ctx.lineWidth = 1.5;
      roundRect(ctx, fx0, fy, fw, 9, 5);
      ctx.stroke();
    }

    // Combo.
    if (sim.combo > 0) {
      ctx.textAlign = 'center';
      const pulse = 1 + 0.12 * Math.sin(timeS * 12);
      ctx.save();
      ctx.translate(w / 2, top + 6);
      ctx.scale(pulse, pulse);
      ctx.font = '900 27px "Arial Black", system-ui, sans-serif';
      outlined(ctx, `x${(1 + sim.combo * 0.25).toFixed(2)}`, 0, 0, this.theme.accent, 27);
      ctx.restore();
    }

    // Lives, for modes that have them.
    if (Number.isFinite(sim.lives)) {
      const left = sim.lives - sim.crashes;
      ctx.textAlign = 'center';
      for (let i = 0; i < sim.lives; i++) {
        const cx = w / 2 - (sim.lives - 1) * 11 + i * 22;
        const cy = top + 46;
        ctx.fillStyle = i < left ? '#ff4f7a' : 'rgba(255,255,255,0.2)';
        ctx.beginPath();
        ctx.arc(cx, cy, 5.5, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // Trail progress.
    if (sim.track.finite) {
      const py = this.h - this.insetBottom - 8;
      ctx.fillStyle = 'rgba(8,10,20,0.45)';
      ctx.fillRect(16, py, w - 32, 4);
      ctx.fillStyle = this.theme.accent;
      ctx.fillRect(16, py, (w - 32) * sim.progress, 4);
    }

    if (sim.offroad) {
      ctx.textAlign = 'center';
      ctx.font = '900 18px "Arial Black", system-ui, sans-serif';
      outlined(ctx, 'OFF TRAIL', w / 2, this.h * 0.28, '#ff9a3c', 18);
    }

    this.drawStick(ctx);
    ctx.restore();
    void frame;
    void shade;
  }

  private drawStick(ctx: CanvasRenderingContext2D): void {
    const s = this.input.stick;
    if (!s.active) return;
    const rect = this.canvas.getBoundingClientRect();
    const ax = s.ax - rect.left;
    const ay = s.ay - rect.top;
    const tx = s.tx - rect.left;
    const ty = s.ty - rect.top;
    ctx.save();
    ctx.strokeStyle = 'rgba(255,255,255,0.28)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(ax, ay, 46, 0, Math.PI * 2);
    ctx.stroke();
    const dx = Math.max(-46, Math.min(46, tx - ax));
    const dy = Math.max(-46, Math.min(46, ty - ay));
    ctx.fillStyle = 'rgba(255,255,255,0.34)';
    ctx.beginPath();
    ctx.arc(ax + dx, ay + dy, 22, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  private drawCountdown(ctx: CanvasRenderingContext2D): void {
    const n = Math.ceil(this.countdownMs / 1000);
    const frac = 1 - (this.countdownMs % 1000) / 1000;
    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.globalAlpha = 0.35 + 0.65 * (1 - frac);
    const size = 90 + frac * 30;
    ctx.font = `900 ${size}px "Arial Black", system-ui, sans-serif`;
    outlined(ctx, n > 0 ? String(n) : 'DROP IN', this.w / 2, this.h * 0.42, this.theme.accent, size);
    ctx.restore();
  }

  dispose(): void {
    this.stop();
    this.input.detach();
    this.audio.dispose();
  }
}

function outlined(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  fill: string,
  size: number,
): void {
  ctx.lineWidth = Math.max(2.5, size * 0.16);
  ctx.strokeStyle = 'rgba(6,8,18,0.72)';
  ctx.lineJoin = 'round';
  ctx.strokeText(text, x, y);
  ctx.fillStyle = fill;
  ctx.fillText(text, x, y);
}
